import { TrueBase, css, html } from "../TrueBase/TrueBase";

/**
   * @customelement true-image
   * @description
   * This is a basic image component used for simple images like logos etc.
   *
   * ### Properties
   * @prop - {String} src - the source of the image.
   * @prop - {String} sizing - Set how the image should fill its parent. Choices from 'cover' and 'contain'.
   * @prop - {Boolean} lazyload - Native lazy loading. You can choose from 'auto', 'lazy' and 'eager' (https://web.dev/native-lazy-loading/). Only supported for modern browsers. 
   * @prop - {String} altText - alt text for the image.
   * @prop - {String} height - Height for the image.
   *
   * ### Styling
   * Css Variables | Description | Default
  ----------------|-------------|----------
  `--image-height` | The height of the image | `100%`
   */
export class TrueImage extends TrueBase {
  static get properties() {
    return {
      imageSrc: {
        type: String,
      },
      altText: {
        type: String,
      },
      sizing: {
        type: String,
        reflect: true,
      },
      lazyload: {
        type: String,
      },
      height: {
        type: Number,
      },
      width: {
        type: Number,
      },
      image: {
        type: Object,
        attribute: false,
        reflect: false,
      },
    };
  }

  constructor() {
    super();
    this.lazyload = "lazy";
    this.altText = "";
  }

  render() {
    return html`
      <picture>
        <source srcset="${this.imageSrc}" type="image/webp" />
        <source srcset="${this.imageSrc}" type="image/jpeg" />
        <img
          id="image"
          .loading="${this.lazyload}"
          width="${this.width}"
          height="${this.height}"
          alt="${this.altText}"
          src="${this.imageSrc}"
          importance=${this.lazyload == "eager" ? "high" : "low"}
        />
      </picture>
    `;
  }

  firstUpdated() {
    this.image = this.shadowRoot.querySelector("#image");
    if (!this.width) {
      this.width = this.image.clientWidth;
    }
    if (!this.height) {
      this.height = this.image.clientHeight;
    }
  }

  static get styles() {
    return css`
      :host {
        display: var(--image-display, flex);
        overflow: hidden;
      }

      picture {
        display: inline-block;
      }

      img {
        max-width: var(--image-max-width, 100%);
        display: block;
        height: var(--image-height, 400px);
        width: var(--image-width, 100%);
        filter: var(--image-filter, none);
      }

      :host([sizing="cover"]) img {
        position: absolute;
        object-fit: cover;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        height: var(--image-height, 400px);
        width: 100%;
        z-index: var(--image-z-index, -2);
      }

      :host([sizing="contain"]) img {
        position: absolute;
        object-fit: contain;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        height: var(--image-height, 400px);
        width: 100%;
        z-index: var(--image-z-index, -2);
      }
    `;
  }
}

window.customElements.get("true-image") ||
  window.customElements.define("true-image", TrueImage);
